import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useQueryParams from 'hooks/useQueryParams'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'

const AREA_PAGE_SIZE = 5

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
`

const ViewMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${rem(8)};
`

interface Props {
  areas: Array<App.ExperienceAreaFacet>
  hideCounts?: boolean
  onAreaSelect?: () => void
}

function ExperienceSearchAreaFilter({ areas, hideCounts = false, onAreaSelect }: Props) {
  const intl = useIntl()
  const navigate = useNavigate()
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const queryParams = useQueryParams()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [visibleAreaCount, setVisibleAreaCount] = useState(AREA_PAGE_SIZE)
  const areaListKey = useMemo(
    () => areas.map(area => area.placeId).join('|'),
    [areas],
  )

  useEffect(() => {
    setVisibleAreaCount(AREA_PAGE_SIZE)
  }, [areaListKey])

  const visibleAreas = useMemo(
    () => areas.slice(0, visibleAreaCount),
    [areas, visibleAreaCount],
  )
  const hasMoreAreas = visibleAreaCount < areas.length

  const onSelectArea = useCallback((area: App.ExperienceAreaFacet) => {
    trackSearchFilter({
      label: area.name,
      type: 'area',
      value: 'selected',
    })

    const parentDestinationId = queryParams.get('areaParentDestinationId') ?? queryParams.get('destinationId')
    const parentDestinationName = queryParams.get('areaParentDestinationName') ?? queryParams.get('destinationName')
    const searchItem: App.SearchDestination = {
      searchType: 'destination',
      value: area.placeId,
      format: {
        mainText: area.name,
        secondaryText: area.nameFull !== area.name ? area.nameFull : undefined,
      },
      destinationType: area.type,
    }

    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem,
    })

    const nextQueryParams = new URLSearchParams(queryParams)
    if (parentDestinationId && parentDestinationName && parentDestinationId !== area.placeId) {
      nextQueryParams.set('areaParentDestinationId', parentDestinationId)
      nextQueryParams.set('areaParentDestinationName', parentDestinationName)
    }
    nextQueryParams.set('destinationId', area.placeId)
    nextQueryParams.set('destinationName', area.name)
    nextQueryParams.delete('experienceId')
    nextQueryParams.delete('experienceName')

    navigate({ search: nextQueryParams.toString() }, { replace: true })
    onAreaSelect?.()
  }, [navigate, globalSearchDispatch, onAreaSelect, queryParams, trackSearchFilter])

  const onViewMore = useCallback(() => {
    setVisibleAreaCount(count => Math.min(count + AREA_PAGE_SIZE, areas.length))
  }, [areas.length])

  if (!areas.length) {
    return null
  }

  return (
    <ListContainer>
      {visibleAreas.map(area => (
        <ClickableListItem
          key={area.placeId}
          title={area.name}
          size="medium"
          onClick={() => onSelectArea(area)}
        >
          {!hideCounts && (
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'NrT32E2qjn901DUY9j_-',
                  defaultMessage: '({count})',
                  description: 'Count of matching experiences shown next to an area filter option',
                },
                { count: area.count },
              )}
            </BodyText>
          )}
        </ClickableListItem>
      ))}
      {hasMoreAreas && (
        <ViewMoreContainer>
          <TextButton kind="tertiary" size="small" onClick={onViewMore}>
            {intl.formatMessage({
              id: 'zCLCve1yDivZsURXttWw',
              defaultMessage: 'View more',
              description: 'Button to reveal the next set of area filter options in the experience search filters',
            })}
          </TextButton>
        </ViewMoreContainer>
      )}
    </ListContainer>
  )
}

export default ExperienceSearchAreaFilter
